import { useEffect, useState } from 'react';
import { Transition } from 'react-transition-group';
import styles from 'styles/modules/newsAlert/Response.module.scss';
import SmartText from 'components/SmartText';
import LimitedTextArea from 'components/Sidescreen/LimitedTextArea';
import Link from 'next/link';
import Button from 'components/Button';
import ReactDOMServer from 'react-dom/server';

import { useDispatch, useSelector } from 'react-redux';
import { authSelector } from 'store/auth';
import {
    orderAlertResponse,
    removeAlertResponse,
    updateAlertResponse,
} from 'store/alert';
import {
    handleHidePopup,
    handleShowPopup,
    setPopupConfig,
    setPopupLoading,
} from 'store/ux';
import { common, news_alerts } from 'lang/en.json';
import ProfileTag from 'components/ProfileTag';
import OptionSelect from 'components/OptionSelect';
import { getReq } from 'api';
import Pin from 'components/Svg/Pin';
import Tick from 'components/Svg/Tick';
import LoginInfo from 'components/LoginInfo';
import { RESPONSE_MAX_CHARACTERS } from './AddResponse';
import { br2nl } from '../../helpers/strings';
import Quote2 from 'components/Svg/Quote2';
import ResponseQuote from './ResponseQuote';
import copyFormatted from 'utils/copyFormattedHTML';
import SvgPlatinumBadge from '../Svg/PlatinumBadge';

import EmbedCode from 'components/Svg/EmbedCode';

// A deactivated responder's page answers 404: the card keeps the name, minus the link.
const NoLink = ({ children }) => children;

const transitionStyles = {
    entering: {
        opacity: 0,
        position: 'absolute',
        transform: 'translateX(30px)',
    },
    entered: { opacity: 1, transform: 'translateX(0)' },
    exiting: { opacity: 0, transform: 'translateX(0px)' },
    exited: { opacity: 0, transform: 'translateX(30px)' },
};

const Response = ({
    alertId,
    alertSlug,
    profile: {
        id: companyId,
        name,
        email,
        phone,
        logo,
        slug,
        website,
        position,
        employer: profileCompany,
        addresses,
        deactivated,
    },
    id,
    body,
    video,
    index: responseIndex,
    expert,
    status,
    isStorySource,
    author: { plan, isVerified },
}) => {
    const dispatch = useDispatch();
    const [contacts, setContacts] = useState(false);
    const [experts, setExperts] = useState([]);
    const [profileAddresses, setProfileAddresses] = useState(null);
    const { isAdmin, isJournalist, isFTEditor, isAuthenticated, company } =
        useSelector(authSelector);

    const [html, setHtml] = useState(body);
    const [sentHtml, setSentHtml] = useState(body);
    const [edit, setEdit] = useState(false); // TODO: false
    const [copied, setCopied] = useState(false);
    const [iframeCopied, setIframeCopied] = useState(false);
    const [shareClicked, setShareClicked] = useState(false);

    const [expertSelected, setExpertSelected] = useState({
        value: expert ? expert.name : news_alerts.responses.sendWithoutExperts,
        ...(expert && {
            id: expert.id,
            ...(expert.image && { image: expert.image }),
            ...expert,
        }),
    });

    useEffect(() => {
        async function fetchExperts() {
            const res = await getReq(`/profiles/${companyId}/experts`);
            if (res.status === 200) {
                setExperts(res.data);
            }
        }

        async function fetchProfileData() {
            try {
                const res = await getReq(`/profiles/${companyId}`);
                if (res.status === 200) {
                    setProfileAddresses(res.data.data.addresses);
                }
            } catch (error) {
                console.error('Error fetching profile data:', error);
            }
        }

        if (isAdmin) {
            fetchExperts();
        }

        // Fetch profile data to get addresses
        if (companyId) {
            fetchProfileData();
        }
    }, [companyId]);

    // The expert who raised the alert is its Story Source: labelled like a pin and listed
    // first by the API whether or not an editor pinned them, never also a Star Quote.
    const pinned = responseIndex !== null || isStorySource;
    const pinLabel = isStorySource ? 'Story Source' : 'Star Quote';

    const handleClick = () => {
        setContacts(!contacts);
    };

    useEffect(() => {
        setHtml(body);
        setSentHtml(body);
        setExpertSelected({
            value: expert
                ? expert.name
                : news_alerts.responses.sendWithoutExperts,
            ...(expert && {
                id: expert.id,
                ...(expert.image && { image: expert.image }),
                ...expert,
            }),
        });
    }, [body, id]);

    const updateResponse = async () => {
        setSentHtml(html);
        await dispatch(
            updateAlertResponse({
                alertId,
                responseId: id,
                body: html,
                expert: expertSelected.id ? expertSelected.id : null,
            })
        );
        setEdit(false);
    };

    const removeResponse = (id) => {
        dispatch(handleShowPopup());
        dispatch(
            setPopupConfig({
                onConfirm: async () => {
                    dispatch(setPopupLoading(true));
                    await dispatch(
                        removeAlertResponse({ alertId, responseId: id })
                    );
                    dispatch(setPopupLoading(false));
                    return dispatch(handleHidePopup());
                },
                onCancel: () => dispatch(handleHidePopup()),
                title: 'Remove response?',
                confirmTitle: 'Confirm',
                cancelTitle: 'Cancel',
            })
        );
    };

    const pinToTop = async () => {
        dispatch(handleShowPopup());
        dispatch(
            setPopupConfig({
                onConfirm: async () => {
                    dispatch(setPopupLoading(true));
                    if (edit) {
                        updateResponse();
                    }
                    await dispatch(
                        orderAlertResponse({
                            alertId,
                            responseId: id,
                            index: 0,
                        })
                    );
                    setEdit(false);
                    dispatch(setPopupLoading(false));
                    return dispatch(handleHidePopup());
                },
                onCancel: () => dispatch(handleHidePopup()),
                title: 'Pin to Top?',
                confirmTitle: 'Confirm',
                cancelTitle: 'Cancel',
            })
        );
    };

    const unpin = async () => {
        dispatch(handleShowPopup());
        dispatch(
            setPopupConfig({
                onConfirm: async () => {
                    dispatch(setPopupLoading(true));
                    await dispatch(
                        orderAlertResponse({
                            alertId,
                            responseId: id,
                            index: -1,
                        })
                    );
                    setEdit(false);
                    dispatch(setPopupLoading(false));
                    return dispatch(handleHidePopup());
                },
                onCancel: () => dispatch(handleHidePopup()),
                title: 'Unpin response?',
                confirmTitle: 'Confirm',
                cancelTitle: 'Cancel',
            })
        );
    };

    const handleEmbed = () => {
        const baseUrl = window.location.origin;
        const iframeCode = `<iframe 
    id="np-embed-${id}"
    src="${baseUrl}/embed/alerts/${alertId}/responses/${id}" 
    width="500px"
    style="border: none; width: 100%; max-width: 500px; border-radius: 12px; overflow: hidden;"
    scrolling="no"></iframe>
<script>
    window.addEventListener('message', function(e) {
        if (e.data.type === 'resize') {
            document.getElementById('np-embed-${id}').style.height = e.data.height + 'px';
        }
    });
</script>`;
        navigator.clipboard.writeText(iframeCode);
        setIframeCopied(true);
        setTimeout(() => setIframeCopied(false), 2000);
    };

    const handleShare = async () => {
        try {
            const shareUrl = `${window.location.origin}/embed/alerts/${alertId}/responses/${id}`;
            await navigator.clipboard.writeText(shareUrl);
            setShareClicked(true);
            setTimeout(() => setShareClicked(false), 2000);
        } catch (err) {
            console.error('Copy to clipboard failed:', err);
        }
    };

    const image =
        (expertSelected.id &&
            expertSelected.image &&
            expertSelected.image.path) ||
        (logo && logo.path);

    const showFooter =
        (edit && experts?.length > 0) ||
        (isAuthenticated && company === slug && status === 'active') ||
        isAdmin === true ||
        isJournalist === true ||
        isFTEditor === true;
    const showFooterButtons =
        isAdmin === true ||
        isJournalist === true ||
        isFTEditor === true ||
        (isAuthenticated && company === slug && status === 'active');
    // The API lets only an admin or the responding profile edit a response.
    const canEdit =
        isAdmin === true ||
        (isAuthenticated && company === slug && status === 'active');

    const ProfileLink = deactivated ? NoLink : Link;

    const handleQuote = () => {
        const result = `
        <meta charset="utf-8">
        ${ReactDOMServer.renderToString(
            <ResponseQuote
                company={{
                    name: profileCompany || name,
                    expertName: expert?.name || name,
                    expertPosition: expert?.position || position,
                    website,
                    slug,
                    deactivated,
                    city:
                        profileAddresses && profileAddresses.length > 0
                            ? profileAddresses[0].city
                            : null,
                }}
                response={{ body }}
                alertSlug={alertSlug}
                planType={plan?.type}
            />
        )}
        `;

        copyFormatted(result);
        setCopied(true);

        setTimeout(() => {
            setCopied(false);
        }, 1000);
    };

    return (
        <div
            id={id}
            className={[
                'border-accent',
                styles.wrapper,
                plan.type === 'contributing_editor'
                    ? styles.contributingEditorWrapper
                    : '',
                //TODO later after we will have design
                //isFTEditor ? styles.ftEditorWrapper : '',
                plan.type === 'legend' ? styles.legendWrapper : '',
                plan.type === 'platinum' ? styles.platinumWrapper : '',
                pinned && plan.type === 'contributing_editor'
                    ? styles.pinnedContributingEditor
                    : '',
                pinned && isFTEditor ? styles.pinnedFTEditor : '',
                pinned && plan.type === 'legend' ? styles.pinnedLegend : '',
            ].join(' ')}
        >
            <div className={styles.controls}>
                {pinned &&
                    (plan.type === 'free' || plan.type === 'premium') && (
                        <div className={styles.pinFree}>
                            <span>{pinLabel}</span>
                            <Pin fill={'#fff'} />
                        </div>
                    )}
                {pinned && plan.type === 'contributing_editor' && (
                    <div className={styles.pinContributingEditor}>
                        <span>{pinLabel}</span>
                        <Pin fill={'#fff'} />
                    </div>
                )}

                {pinned && isFTEditor && (
                    //TODO later after we will have design
                    //<div className={styles.pinFTEditor}>
                    <div className={styles.pinContributingEditor}>
                        <span>{pinLabel}</span>
                        <Pin fill={'#fff'} />
                    </div>
                )}

                {pinned && plan.type === 'legend' && (
                    <div className={styles.pinLegend2}>
                        <span>{pinLabel}</span>
                        <Pin fill={'#fff'} />
                    </div>
                )}

                {pinned && plan.type === 'platinum' && (
                    <div className={styles.pinPlatinum}>
                        <span>{pinLabel}</span>
                        <Pin fill={'#fff'} />
                    </div>
                )}

                <div
                    onClick={isAuthenticated ? handleQuote : null}
                    className={[
                        styles.copy,
                        styles.pin,
                        !isAuthenticated && 'loginLocked',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    data-hint={isAuthenticated ? undefined : common.loginToCopy}
                >
                    <span>{!copied ? 'Copy' : 'Copied to clipboard'}</span>
                    <Quote2 fill="#fff" />
                </div>

                {video && (
                    <>
                        <div
                            onClick={handleEmbed}
                            className={[styles.copy, styles.pin].join(' ')}
                        >
                            <span>
                                {!iframeCopied ? 'Embed' : 'Embed code copied!'}
                            </span>
                            <EmbedCode fill="#fff" />
                        </div>

                        <div
                            onClick={handleShare}
                            className={[styles.copy, styles.pin].join(' ')}
                        >
                            <span>
                                {!shareClicked ? 'Copy link' : 'Link copied!'}
                            </span>
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 6.65685 16.3431 8 18 8Z"
                                    stroke="#fff"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M6 15C7.65685 15 9 13.6569 9 12C9 10.3431 7.65685 9 6 9C4.34315 9 3 10.3431 3 12C3 13.6569 4.34315 15 6 15Z"
                                    stroke="#fff"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M18 22C19.6569 22 21 20.6569 21 19C21 17.3431 19.6569 16 18 16C16.3431 16 15 17.3431 15 19C15 20.6569 16.3431 22 18 22Z"
                                    stroke="#fff"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M8.59 13.51L15.42 17.49"
                                    stroke="#fff"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M15.41 6.51L8.59 10.49"
                                    stroke="#fff"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    </>
                )}

                {plan.type === 'legend' ? (
                    <div className={styles.pinLegend}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="35"
                            height="45"
                            fill="none"
                        >
                            <path
                                fill="var(--color-blue)"
                                d="M0 29.647V0h35v29.647L17.246 45 0 29.647Z"
                            />
                            <path
                                fill="#fff"
                                d="m23.462 21.938-.288 1.251a.563.563 0 0 1-.549.436h-11.25a.563.563 0 0 1-.548-.436l-.289-1.252h12.924Z"
                            />
                            <path
                                fill="#fff"
                                d="m24.86 15.877-1.138 4.935H10.278l-1.14-4.935a.563.563 0 0 1 .828-.615l3.482 1.99 3.083-4.626a.564.564 0 0 1 .913-.034l3.616 4.65 2.94-1.96a.564.564 0 0 1 .86.595Z"
                            />
                        </svg>
                    </div>
                ) : null}
                {plan.type === 'platinum' ? (
                    <div className={styles.platinumBadge}>
                        <SvgPlatinumBadge />
                    </div>
                ) : null}
            </div>
            <br />

            <div
                className={['border-gray-bottom', styles.headerWrapper].join(
                    ' '
                )}
            >
                <header>
                    <ProfileLink href={`/${slug}`}>
                        <a className={['mb-s', styles.header].join(' ')}>
                            {image && <ProfileTag {...{ plan, image }} />}
                            <div className={styles.personal}>
                                <h5 className={styles.name}>
                                    {expertSelected.id
                                        ? expertSelected.name
                                        : name}
                                    {Boolean(isVerified) && (
                                        <span className={styles.verifiedBadge}>
                                            <Tick fill="#fff" />
                                            Verified
                                        </span>
                                    )}
                                </h5>
                                <h6 className={styles.expert}>
                                    {expertSelected.id
                                        ? expertSelected.position || position
                                            ? `${
                                                  expertSelected.position ||
                                                  position
                                              } at `
                                            : ''
                                        : position
                                        ? `${position} at `
                                        : ''}
                                    {(expertSelected.id
                                        ? expertSelected.position || position
                                        : position) && (
                                        <>
                                            {profileAddresses &&
                                            profileAddresses.length > 0 &&
                                            profileAddresses[0].city
                                                ? `${profileAddresses[0].city}-based `
                                                : ''}
                                            <span>
                                                {profileCompany || name}
                                            </span>
                                        </>
                                    )}
                                </h6>
                            </div>
                        </a>
                    </ProfileLink>
                </header>

                {!isAuthenticated ? (
                    <div className={styles.contactsLocked}>
                        {/* A placeholder, not the expert's details: the API sends none here. */}
                        <span
                            aria-hidden="true"
                            className="loginBlur"
                            data-nosnippet
                        >
                            name@company.co.uk
                        </span>
                        <LoginInfo label="Why are the contact details hidden?">
                            Contact details are shown to logged-in members.
                        </LoginInfo>
                    </div>
                ) : (
                    <div className={styles.contacts}>
                        <Transition in={!contacts} timeout={400} unmountOnExit>
                            {(state) => (
                                <button
                                    type="button"
                                    onClick={handleClick}
                                    className={styles.button}
                                    style={{ ...transitionStyles[state] }}
                                >
                                    <span>
                                        {news_alerts.responses.show_contacts}
                                    </span>
                                </button>
                            )}
                        </Transition>

                        <Transition in={contacts} timeout={400} unmountOnExit>
                            {(state) => (
                                <div
                                    className={styles.contacts}
                                    style={{ ...transitionStyles[state] }}
                                >
                                    <a href={`mailto:${email}`}>{email}</a>
                                    <a href={`tel:${phone}`}>{phone}</a>
                                </div>
                            )}
                        </Transition>
                    </div>
                )}
            </div>

            {video && (
                <div className={styles.videoWrapper}>
                    <video
                        controls
                        className={styles.video}
                        src={
                            video.startsWith('http')
                                ? video
                                : `https://${video}`
                        }
                    >
                        Your browser does not support the video tag.
                    </video>
                </div>
            )}

            {edit ? (
                <LimitedTextArea
                    classes={[styles.responseBox]}
                    placeholder="Write your masterpiece here. And remember, no waffle."
                    callback={(value) => setHtml(value)}
                    value={body}
                    limit={RESPONSE_MAX_CHARACTERS}
                />
            ) : (
                sentHtml && (
                    <SmartText
                        quotes={true}
                        bg={'#fafafa'}
                        height={180}
                        change={sentHtml}
                        data={sentHtml}
                    />
                )
            )}

            {showFooter && (
                <div
                    className={[
                        styles.footer,
                        edit ? styles.footerActive : '',
                    ].join(' ')}
                >
                    {edit && experts?.length > 0 && (
                        <div className={styles.experts}>
                            <OptionSelect
                                options={experts}
                                value={expertSelected}
                                setValue={setExpertSelected}
                                defaultValue={
                                    news_alerts.responses.sendWithoutExperts
                                }
                            />
                        </div>
                    )}

                    {showFooterButtons && (
                        <div className={styles.footerButtons}>
                            {isAdmin === true ||
                            isFTEditor === true ||
                            (isJournalist === true && responseIndex !== 0) ? (
                                <Button
                                    // theme={'secondary'}
                                    title={'Pin to Top'}
                                    onClick={() => pinToTop(id)}
                                    classes={[styles.buttonPin]}
                                />
                            ) : null}
                            {responseIndex !== null &&
                            responseIndex !== undefined &&
                            (isJournalist === true ||
                                isFTEditor === true ||
                                isAdmin === true) ? (
                                <Button
                                    theme={'alert'}
                                    title={'UNPIN'}
                                    onClick={() => unpin(id)}
                                    classes={[styles.buttonPin]}
                                />
                            ) : null}

                            {(isAuthenticated &&
                                company === slug &&
                                status === 'active') ||
                            isAdmin === true ||
                            isJournalist === true ||
                            isFTEditor === true ? (
                                <>
                                    {canEdit && (
                                        <Button
                                            theme={'secondary'}
                                            title={edit ? 'Cancel' : 'Edit'}
                                            onClick={() => {
                                                setHtml(body);
                                                setEdit(!edit);
                                            }}
                                        />
                                    )}

                                    {(!edit && isAdmin === true) ||
                                    isJournalist === true ||
                                    isFTEditor === true ? (
                                        <Button
                                            theme={'danger'}
                                            title={'Remove'}
                                            onClick={() => removeResponse(id)}
                                        />
                                    ) : null}

                                    {edit && (
                                        <Button
                                            title={common.save}
                                            disabled={
                                                br2nl(html).length >
                                                RESPONSE_MAX_CHARACTERS
                                            }
                                            onClick={updateResponse}
                                        />
                                    )}
                                </>
                            ) : null}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Response;
